import _ from "lodash";
import queryString from "query-string";
import { useEffect, useState, useMemo } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { useClient } from "./use-client";
import Elasticsearch, { IElasticsearchSort } from "../utils/elasticsearch";
import { DefaultViewName } from "../utils/variables";

export const useStreamResourcePageEffects = (
  dataProvider: any,
  selectedView?: any,
  onScrollTop?: any,
  clearFilterPath?: string,
  supportsColumnSelection?: boolean,
  handleViewUpdatesFromURL?: any,
) => {
  const history = useHistory();
  const location = useLocation();

  // Scroll back to top when filters change
  useEffect(() => {
    if (dataProvider.omnifilters.dirty) {
      onScrollTop && onScrollTop();
      dataProvider.streamQuery.scrollTop();
    }
  }, [dataProvider.omnifilters.dirty]);

  // Update URL when view changes
  useEffect(() => {
    const query = queryString.parse(location.search, { arrayFormat: "bracket", parseBooleans: true });

    if (clearFilterPath && _.isEmpty(dataProvider.omnifilters.filterState) && !query.clearfilters) {
      console.log("Need to redirect to clear filter url");
      window.location.replace(clearFilterPath);
    }

    const currentQuery = query;
    const filterState = { ...dataProvider.omnifilters.filterState };
    delete filterState.q_fields;
    const nextQuery = supportsColumnSelection
      ? Object.assign(
          {},
          filterState,
          dataProvider.sort,
          { columns: JSON.stringify(dataProvider.columns) },
          { currentViewName: dataProvider.currentViewName },
        )
      : Object.assign({}, filterState, dataProvider.sort);
    if (_.isEqual(currentQuery, nextQuery)) {
      return;
    }
    const applicationURL = {
      pathname: location.pathname,
      search: queryString.stringify(nextQuery, { arrayFormat: "bracket" }),
    };
    history.push(applicationURL);
    localStorage.setItem("applicationURL", JSON.stringify(applicationURL));
  }, [dataProvider.omnifilters.filterState, dataProvider.sort, dataProvider.columns, dataProvider.currentViewName]);

  // Update view from URL
  useEffect(() => {
    if (handleViewUpdatesFromURL) {
      const query = queryString.parse(location.search, { arrayFormat: "bracket", parseBooleans: true });
      handleViewUpdatesFromURL(query, dataProvider, selectedView);
    } else {
      const query = queryString.parse(location.search, { arrayFormat: "bracket", parseBooleans: true });
      const { field, direction } = query;
      dataProvider.setSort(
        Object.assign(
          {},
          dataProvider.defaultSort,
          selectedView?.attributes?.sort,
          _.pickBy({ field, direction }, _.identity),
        ),
      );
    }
  }, []);
};

export const useStreamResource = (
  _key: string,
  queryKey: string,
  omnifilters: any,
  streamQueryFn: (
    key: string,
    size: number,
    refreshCursor: any,
    setRefreshCursor: any,
    filter: any,
    reverseSort: any,
    sort: any,
    enabled: any,
    hits: any,
    setHits: any,
  ) => any,
  rowBuilder: (hits: any) => any,
  defaultSort: IElasticsearchSort,
  defaultRefreshCursor: any,
  defaultColumns?: any,
  resetHitsOnFilterChange?: boolean,
  idField = "_id",
) => {
  const { site } = useParams<{ site: string }>();
  const { clientId } = useClient(site);
  const [sort, setSort] = useState<IElasticsearchSort>(defaultSort);
  const [columns, setColumns] = useState<any>(defaultColumns);
  const [hits, setHits] = useState<any[]>([]);

  const [currentViewName, setCurrentViewName] = useState<any>(
    localStorage.getItem("currentViewName") ?? DefaultViewName,
  );

  const [refreshCursor, setRefreshCursor] = useState(defaultRefreshCursor);
  const filter = useMemo(
    () => ({
      cityId: clientId,
      ...omnifilters.query,
    }),
    [clientId, omnifilters.query],
  );

  const streamSort = useMemo(() => Elasticsearch.sort(sort, idField), [sort]);
  const streamReverseSort = useMemo(() => Elasticsearch.reverseSort(sort, idField), [sort]);

  const streamQuery = streamQueryFn(
    queryKey,
    100,
    refreshCursor,
    setRefreshCursor,
    filter,
    streamReverseSort,
    streamSort,
    !!clientId,
    hits,
    setHits,
  );

  const setDefaultView = () => {
    setColumns(defaultColumns);
    setCurrentViewName(DefaultViewName);
  };

  const resetHits = () => {
    setHits([]);
    setRefreshCursor(0);
  };

  useEffect(() => {
    localStorage.setItem("currentViewName", currentViewName);
  }, [currentViewName]);

  useEffect(() => {
    setRefreshCursor(hits.length);
  }, [hits.length]);

  // when filters change, empty hits and set refresh cursor to 0
  useEffect(() => {
    if (resetHitsOnFilterChange) resetHits();
  }, [omnifilters.filterState, omnifilters.query]);

  const rows = useMemo(() => rowBuilder(hits), [hits]);

  return {
    streamQuery,
    rows,
    defaultSort,
    sort,
    setSort,
    omnifilters,
    columns,
    setColumns,
    setDefaultView,
    currentViewName,
    setCurrentViewName,
    setRefreshCursor,
    setHits,
    resetHits,
  };
};
